// Kořen aplikace: react-query, globální prvky (toast, dialog), stav přihlášení (GET /ja, cookie
// relace z Entra ID) a směrování: /prihlaseni veřejně, vše ostatní v aplikačním shellu (F1 1.3) —
// Domů na kořenu, obrazovky sekcí podle mapy shell/sekce-cesty.ts.
//
// Router je **datový** (`createBrowserRouter` + `RouterProvider`) s jedinou cestou `*`, která vykreslí
// dosavadní `<Routes>` — cesty ani líné načítání obrazovek (docs/09 §10) se nemění, jen jde použít `useBlocker`:
// přechod na jinou obrazovku s rozepsaným formulářem se zeptá, jestli ho zahodit (rozhodnutí zadavatele
// 24. 9. 2026, 05/473; `formulare/rozepsano.tsx` `HlidacOdchodu`).
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { usePrihlaseni } from './prihlaseni/stav-prihlaseni';
import { RouterProvider } from 'react-router/dom';
import { HlidacOdchodu, PoskytovatelRozepsanych } from './formulare/rozepsano';
import { PoskytovatelDialogu } from './komponenty/Dialog';
import { useEnterJakoTab } from './komponenty/enter-jako-tab';
import { lina, NacitaniCasti } from './komponenty/NacitaniCasti';
import { nactiNaPozadi } from './komponenty/nacti-na-pozadi';
import { PoskytovatelToastu } from './komponenty/Toast';
import { SledovaniHistorie } from './komponenty/zpet';
import { PoskytovatelPrihlaseni } from './prihlaseni/stav-prihlaseni';
import { VyzadujePrihlaseni } from './prihlaseni/VyzadujePrihlaseni';
// etapa 3 část G: posluchač zpráv ze samostatného okna mapy (malý soubor bez kódu sekce — docs/09 §12).
import { NavigaceZOkna } from './sekce/mapa-provozu/okno-zpravy';
import { StrankaSekce } from './sekce/StrankaSekce';
import { Shell } from './shell/Shell';
import { Domu } from './stranky/Domu';

// Stránky, které většina uživatelů neotevře — veřejné odkazy pro zákazníky a provozní deník z odkazu —
// se stahují až při otevření (vlastní chunky buildu, jako obrazovky sekcí ve StrankaSekce.tsx).
// Přihlášení vidí jen nepřihlášený — jeho kód se stáhne až tehdy (rozpočet hlavního balíku, `vite.config.ts`).
const Prihlaseni = lina(() => import('./stranky/Prihlaseni').then((m) => m.Prihlaseni));
const StrankaDeniku = lina(() =>
  import('./sekce/iso/denik/StrankaDeniku').then((m) => m.StrankaDeniku),
);
// Kniha jízd služebního vozidla (W82 díl 2) — vstup z Domů a odkaz signálu `kniha_jizd`.
const StrankaKnihyJizd = lina(() =>
  import('./sekce/kniha-jizd/StrankaKnihyJizd').then((m) => m.StrankaKnihyJizd),
);
// Platby na terminálu (3. 10. 2026) — vstup z Můj den skladníka a odkaz signálu; kancelář přesměruje do Účetnictví.
const StrankaPlatebTerminalu = lina(() =>
  import('./sekce/terminal/StrankaPlatebTerminalu').then((m) => m.StrankaPlatebTerminalu),
);
const PodkladyAuditu = lina(() => import('./stranky/PodkladyAuditu').then((m) => m.PodkladyAuditu));
const PortalPodkladu = lina(() => import('./stranky/PortalPodkladu').then((m) => m.PortalPodkladu));
const PortalKlienta = lina(() => import('./stranky/PortalKlienta').then((m) => m.PortalKlienta));
const VerejnyPodpis = lina(() => import('./stranky/VerejnyPodpis').then((m) => m.VerejnyPodpis));
const VerejnaReklamace = lina(() =>
  import('./stranky/VerejnaReklamace').then((m) => m.VerejnaReklamace),
);
const VerejnyDotaznik = lina(() =>
  import('./stranky/VerejnyDotaznik').then((m) => m.VerejnyDotaznik),
);
// etapa 3 část G: samostatné okno mapy provozu — stejný chunk jako sekce Mapa provozu (StrankaSekce.tsx).
const MapaProvozuOkno = lina(() =>
  import('./sekce/mapa-provozu/MapaProvozu').then((m) => m.MapaProvozuOkno),
);
// 05/2173 b: samostatné okno mapy zákazníků — filtry seznamu v adrese, bez GPS.
const MapaZakaznikuOkno = lina(() =>
  import('./sekce/zakaznici/MapaZakaznikuOkno').then((m) => m.MapaZakaznikuOkno),
);

/** Veřejná stránka mimo shell: celostránkový tichý stav, dokud se kód stahuje. */
function Verejna({ children }: { children: ReactNode }) {
  return <NacitaniCasti celaStranka>{children}</NacitaniCasti>;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

/** Cesty aplikace — společné pro App i testy (bez routeru; hlídač odchodu jen pod datovým routerem). */
export function CestyAplikace() {
  return (
    <>
      <HlidacOdchodu />
      {/* etapa 3 část G: odkazy ze samostatného okna mapy provozu otevře hlavní okno. */}
      <NavigaceZOkna />
      {/* Kam vede „Zavřít“ obrazovek s víc kroky nebo s uklizeným odkazem (komponenty/zpet.ts). */}
      <SledovaniHistorie />
      <Routes>
        <Route
          path="/prihlaseni"
          element={
            <Verejna>
              <Prihlaseni />
            </Verejna>
          }
        />
        {/* Veřejný podpis smlouvy nebo dodatku přes odkaz s tokenem (F7 7.4) — zákazník bez účtu, mimo shell. */}
        <Route
          path="/podpis/:token"
          element={
            <Verejna>
              <VerejnyPodpis />
            </Verejna>
          }
        />
        {/* Portál původce — podklady k evidenci přes odkaz s tokenem (F9 9.20) — zákazník bez účtu, mimo shell. */}
        <Route
          path="/podklady/:token"
          element={
            <Verejna>
              <PortalPodkladu />
            </Verejna>
          }
        />
        {/* Portál klienta agendy s účtem (9.21b, 05/1570 b) — přihlášení e‑mailem a kódem, mimo shell zaměstnanců. */}
        <Route
          path="/klient"
          element={
            <Verejna>
              <PortalKlienta />
            </Verejna>
          }
        />
        {/* Veřejné formuláře ISŘ (F11 11.4) — dotazník spokojenosti (společný i osobní odkaz) a reklamace z webu. */}
        <Route
          path="/formular/dotaznik/:token?"
          element={
            <Verejna>
              <VerejnyDotaznik />
            </Verejna>
          }
        />
        <Route
          path="/formular/reklamace"
          element={
            <Verejna>
              <VerejnaReklamace />
            </Verejna>
          }
        />
        {/* Podklady k auditu u zákazníka přes odkaz s tokenem (F11 11.5) — zákazník bez účtu, mimo shell. */}
        <Route
          path="/podklady-auditu/:token"
          element={
            <Verejna>
              <PodkladyAuditu />
            </Verejna>
          }
        />
        {/* Vše ostatní jen pro přihlášené, uvnitř shellu (lišta + hlavička). */}
        <Route element={<VyzadujePrihlaseni />}>
          {/* etapa 3 část G: samostatné okno mapy provozu (druhý monitor) — přihlášení jako shell, bez menu. */}
          <Route
            path="/mapa-provozu/okno"
            element={
              <NacitaniCasti celaStranka>
                <MapaProvozuOkno />
              </NacitaniCasti>
            }
          />
          <Route
            path="/zakaznici/mapa-okno"
            element={
              <NacitaniCasti celaStranka>
                <MapaZakaznikuOkno />
              </NacitaniCasti>
            }
          />
          <Route element={<Shell />}>
            <Route index element={<Domu />} />
            {/* Zájemci o práci (7. 10. 2026) — statická cesta má přednost před kartou /zamestnanci/{id}. */}
            <Route
              path="/zamestnanci/zajemci"
              element={<StrankaSekce klic="zamestnanci:zajemci" />}
            />
            <Route
              path="/zamestnanci/:id/:zalozka?"
              element={<StrankaSekce klic="zamestnanci" />}
            />
            <Route path="/zakaznici/:id/:zalozka?" element={<StrankaSekce klic="zakaznici" />} />
            {/* Audity u zákazníků (W80, 24. 10. 2026): vlastní sekce — detail, vyplnění na tabletu, naplánování.
                Staré odkazy `/zakazky/audit/…` (zvonek, signály před přesunem) přesměruje na `/audity/…`. */}
            <Route path="/audity/:auditId/:rezim?" element={<StrankaSekce klic="audity" />} />
            <Route path="/zakazky/audit/:auditId/:rezim?" element={<PresmerovaniAuditu />} />
            <Route path="/zakazky/:id/:zalozka?" element={<StrankaSekce klic="zakazky" />} />
            {/* Nabídka na jedné stránce (díl N4b): /nabidky/nova a /nabidky/{id}. */}
            <Route path="/nabidky/:id" element={<StrankaSekce klic="nabidky" />} />
            <Route path="/muj-den/:id/:krok?" element={<StrankaSekce klic="muj_den" />} />
            <Route path="/moje-firmy/:zalozka?" element={<StrankaSekce klic="moje_firmy" />} />
            <Route path="/nastaveni/:zalozka/:id?" element={<StrankaSekce klic="nastaveni" />} />
            <Route path="/sklad/:zalozka/:id?" element={<StrankaSekce klic="sklad" />} />
            <Route path="/vozidla/:id" element={<StrankaSekce klic="vozidla" />} />
            <Route path="/vzory-dokumentu/:id" element={<StrankaSekce klic="vzory_dokumentu" />} />
            <Route
              path="/sklad-evidence/evidence/:zalozka"
              element={<StrankaSekce klic="sklad_evidence:evidence" />}
            />
            <Route
              path="/sklad-evidence/ilno/:zalozka"
              element={<StrankaSekce klic="sklad_evidence:ilno" />}
            />
            {/* Kniha jízd (W82 díl 2): moje knihy, kontrola a dohody; měsíc knihy z odkazu signálu. */}
            <Route
              path="/kniha-jizd/:uzivaniId?/:mesic?"
              element={
                <NacitaniCasti>
                  <StrankaKnihyJizd />
                </NacitaniCasti>
              }
            />
            {/* Provozní deník (F11 11.7) — odkaz z připomínek a signálů; podle práv přesměruje do ISŘ nebo Náš sklad. */}
            <Route
              path="/provozni-denik"
              element={
                <NacitaniCasti>
                  <StrankaDeniku />
                </NacitaniCasti>
              }
            />
            {/* Platby na terminálu bez zakázky (3. 10. 2026) — tablet skladníka a odkaz signálu `terminal_platba_nesparovana`. */}
            <Route
              path="/platby-terminal"
              element={
                <NacitaniCasti>
                  <StrankaPlatebTerminalu />
                </NacitaniCasti>
              }
            />
            <Route path="*" element={<StrankaSekce />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

let router: ReturnType<typeof createBrowserRouter> | null = null;

/**
 * Datový router aplikace: jedna cesta `*` s dosavadními `<Routes>` (05/473 — kvůli `useBlocker`). Jeden na stránku —
 * router poslouchá historii prohlížeče, takže se nesmí zakládat při každém renderu (StrictMode renderuje dvakrát).
 */
export function routerAplikace() {
  router ??= createBrowserRouter([{ path: '*', element: <CestyAplikace /> }]);
  return router;
}

export function App() {
  // Enter = Tab ve všech formulářích (rozhodnutí 9. 10. 2026): fokus na další pole, nikdy neodešle.
  useEnterJakoTab();
  // Rozbalená nabídka výběru se vejde na obrazovku: dolů, nebo nahoru, s vlastním posuvníkem (nález 25. 10. 2026).
  // Kód se stahuje samostatně hned po startu, ať se nezapočítá do rozpočtu hlavního balíku (vite.config.ts).
  useEffect(() => {
    let zrusit: (() => void) | undefined;
    const prerusit = nactiNaPozadi(
      () => import('./komponenty/nabidka-vyberu'),
      (modul) => {
        zrusit = modul.zapnoutPolohuNabidek();
      },
    );
    return () => {
      prerusit();
      zrusit?.();
    };
  }, []);
  // Offline fronta tabletu (4.5): odesílá zápisy na pozadí, jakmile je signál, a originály fotek po wifi.
  // Kód fronty a cache se stáhne hned po startu zvlášť (rozpočet hlavního balíku); PWA ho má v precache i offline.
  useEffect(() => {
    let zastavit: (() => void) | undefined;
    // Po nasazení nové verze může starý chunk chybět — stažení se zopakuje (kontrola #761, N1).
    const prerusit = nactiNaPozadi(
      () => import('./offline/start'),
      (m) => {
        zastavit = m.spustOfflineRezim(queryClient);
      },
    );
    return () => {
      prerusit();
      zastavit?.();
    };
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <PoskytovatelToastu>
        <PoskytovatelDialogu>
          {/* Společné hlídání rozepsaných formulářů (05/412): beforeunload a výčet do dialogů. */}
          <PoskytovatelRozepsanych>
            <PoskytovatelPrihlaseni>
              <RouterProvider router={routerAplikace()} />
            </PoskytovatelPrihlaseni>
          </PoskytovatelRozepsanych>
        </PoskytovatelDialogu>
      </PoskytovatelToastu>
    </QueryClientProvider>
  );
}

/**
 * Audit přes Zakázky (`/zakazky/audit/{id}[/rezim]`): kdo má sekci Audity, jde tam (starý odkaz zvonku a signálů);
 * obchodník bez ní (audity svých zákazníků, 05/3951 a) ho čte dál v Zakázkách.
 */
function PresmerovaniAuditu() {
  const misto = useLocation();
  const { ja } = usePrihlaseni();
  if (!(ja?.sekce ?? []).includes('audity')) return <StrankaSekce klic="zakazky" />;
  return (
    <Navigate
      to={`${misto.pathname.replace(/^\/zakazky\/audit/, '/audity')}${misto.search}`}
      replace
    />
  );
}
